import type { DomainEventSubscription } from '~/domain/events/DomainEventBus';
import { domainEventBus } from '~/domain/events/DomainEventBus';
import { RegistrationMatrixCommittedEvent } from '~/domain/events/RegistrationDomainEvents';
import { asRegistrationId } from '~/domain/changes/ids';
import type { RegistrationService } from '~/domain/services/registration/RegistrationService';
import {
    ApprovedRegistrationError,
    StaleReferenceError,
} from '~/domain/errors/DomainError';

/**
 * Routes committed registration matrix edits into the domain change
 * tracker, so moving a registration is dirty state exactly like a rename.
 *
 * Every matrix commit funnels through
 * {@link RigidRegistration.syncTransformationToDataset} (manual
 * translate/rotate on mouse release and keypress, auto-registration results,
 * undo/redo restores, pre-export syncs), which publishes
 * {@link RegistrationMatrixCommittedEvent} only when the dataset value
 * actually changed. Subscribing here is the single funnel — no per-tool
 * wiring, so a new registration tool cannot silently bypass dirty
 * tracking.
 *
 * Failures are reported loudly (console.error, PHI-safe: domain id only),
 * never swallowed — an untracked matrix edit means the unsaved-changes
 * indication could show "nothing to save" for changed clinical data.
 *
 * Created and disposed with the {@link PatientSession}.
 */
export class RegistrationEditBridge {
    private readonly subscription: DomainEventSubscription;

    constructor(private readonly registrationService: RegistrationService) {
        this.subscription =
            domainEventBus.subscribe<RegistrationMatrixCommittedEvent>(
                RegistrationMatrixCommittedEvent.EVENT_TYPE,
                (event) => this.commit(event.registrationId)
            );
    }

    dispose(): void {
        this.subscription.unsubscribe();
    }

    private commit(registrationId: string): void {
        try {
            this.registrationService.commitMatrixEdit({
                id: asRegistrationId(registrationId),
            });
        } catch (error) {
            if (error instanceof ApprovedRegistrationError) {
                // The engine guards should have refused the edit; the value
                // reached the dataset but is NOT dirty-tracked. Surface it.
                console.error(
                    'RegistrationEditBridge: matrix commit on an approved ' +
                        'registration; the edit is NOT dirty-tracked'
                );
                return;
            }
            if (error instanceof StaleReferenceError) {
                // Commit raced a teardown/removal; report rather than break
                // the publishing tool mid-gesture.
                console.error(
                    'RegistrationEditBridge: matrix commit for a ' +
                        'registration not present in the session; the edit ' +
                        'is NOT dirty-tracked'
                );
                return;
            }
            throw error;
        }
    }
}
